import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Box,
  Button,
  CircularProgress,
  Grid,
  Slider,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";

import { useOrganizationContext } from "../../../../../providers/OrganizationAdmin/OrganizationProvider";
import { updateOrganization } from "../../../../../modules/OrgProf/hook/Organizationhook";
import { showNotification } from "../../../../../constants";
import { Toast } from "../../../../../common/alert";
import { useAuth } from "../../../../../hooks";

/**
 * OrgPrioritiesPanel
 *
 * Admin tool (General Admin → QuickTales → Organization Priorities) to edit the
 * `feedPriority` (1-100, 50 = neutral) for every active organization at once, so
 * the relationships between orgs are visually comparable via sliders.
 *
 * Values are edited locally and only the CHANGED orgs are persisted on Save,
 * which minimizes the background re-index fan-out (onOrgWriteReindex fires per
 * org whose feedPriority changes).
 */
const DEFAULT_PRIORITY = 50;

const OrgPrioritiesPanel = () => {
  const { currentUser } = useAuth();
  const { allOrganization, handleGetAllOrganizations, loading } =
    useOrganizationContext();

  // Working copy of edits, keyed by org id -> feedPriority.
  const [priorities, setPriorities] = useState({});
  const [saving, setSaving] = useState(false);

  // How the org rows are ordered: "alpha" (A→Z) or "value" (highest priority first).
  const [sortMode, setSortMode] = useState("alpha");

  useEffect(() => {
    if (!allOrganization || allOrganization.length === 0) {
      handleGetAllOrganizations();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Active orgs, sorted alphabetically by name (stable regardless of edits).
  const activeOrgs = useMemo(() => {
    return (allOrganization || [])
      .filter((org) => (org?.status || "").toLowerCase() === "active")
      .sort((a, b) =>
        (a?.name || "").localeCompare(b?.name || "", undefined, {
          sensitivity: "base",
        })
      );
  }, [allOrganization]);

  const getLoadedValue = (org) =>
    typeof org.feedPriority === "number" ? org.feedPriority : DEFAULT_PRIORITY;

  // Seed the working copy whenever the loaded org list changes.
  useEffect(() => {
    const seed = {};
    activeOrgs.forEach((org) => {
      seed[org.id] = getLoadedValue(org);
    });
    setPriorities(seed);
  }, [activeOrgs]);

  const changedOrgs = useMemo(
    () =>
      activeOrgs.filter((org) => priorities[org.id] !== getLoadedValue(org)),
    [activeOrgs, priorities]
  );

  // Keep the latest priorities accessible without making the value-sort snapshot
  // depend on them (otherwise the row order would shuffle live while dragging).
  const prioritiesRef = useRef(priorities);
  useEffect(() => {
    prioritiesRef.current = priorities;
  }, [priorities]);

  // The value-sorted row order is a SNAPSHOT: it is recomputed only when the user
  // (re)selects "Priority" sorting or the org list changes — never on every drag
  // tick — so the slider you're holding never jumps out from under the cursor.
  const [valueOrderedOrgs, setValueOrderedOrgs] = useState([]);

  const computeValueOrder = (orgs) =>
    [...orgs].sort((a, b) => {
      const snap = prioritiesRef.current;
      const av = snap[a.id] ?? getLoadedValue(a);
      const bv = snap[b.id] ?? getLoadedValue(b);
      if (bv !== av) return bv - av;
      return (a?.name || "").localeCompare(b?.name || "", undefined, {
        sensitivity: "base",
      });
    });

  useEffect(() => {
    if (sortMode === "value") {
      setValueOrderedOrgs(computeValueOrder(activeOrgs));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortMode, activeOrgs]);

  // Orgs in the order they are rendered.
  const displayedOrgs = sortMode === "value" ? valueOrderedOrgs : activeOrgs;

  const handleSliderChange = (orgId, value) => {
    setPriorities((prev) => ({ ...prev, [orgId]: value }));
  };

  // Fired once when a slider is released. In Priority mode, re-take the value-sort
  // snapshot so the row settles into its new position only after the drag ends.
  const handleSliderCommitted = () => {
    if (sortMode === "value") {
      setValueOrderedOrgs(computeValueOrder(activeOrgs));
    }
  };

  const handleReset = () => {
    const seed = {};
    activeOrgs.forEach((org) => {
      seed[org.id] = getLoadedValue(org);
    });
    setPriorities(seed);
  };

  const handleSave = async () => {
    if (changedOrgs.length === 0) return;
    try {
      setSaving(true);
      await Promise.all(
        changedOrgs.map((org) =>
          updateOrganization(org.id, {
            feedPriority: priorities[org.id],
            updatedBy: currentUser?.id ?? "",
          })
        )
      );
      await handleGetAllOrganizations();
      showNotification(
        `Updated feed priority for ${changedOrgs.length} organization${
          changedOrgs.length === 1 ? "" : "s"
        }. Their feeds will re-index in the background.`
      );
    } catch (error) {
      console.log("error updating org priorities:", error.message);
      Toast("error", "Error updating organization priorities");
    } finally {
      setSaving(false);
    }
  };

  const dirty = changedOrgs.length > 0;

  return (
    <Box>
      {/* Header / actions */}
      <Box
        sx={{
          mb: 3,
          p: 2,
          border: "1px solid #e0e0e0",
          borderRadius: 2,
          backgroundColor: "#fafafa",
        }}
      >
        <Box sx={{ fontWeight: 600, mb: 0.5 }}>Organization Feed Priorities</Box>
        <Box sx={{ fontSize: 13, color: "#666", mb: 1.5 }}>
          Adjust each active organization's feed priority (1–100, 50 = neutral).
          Orgs above 50 are boosted in the Listen feed; below 50 are
          deprioritized. Only changed organizations are saved, and their feeds
          re-index automatically.
        </Box>
        <Box sx={{ display: "flex", gap: 2, alignItems: "center" }}>
          <Button
            variant="outlined"
            disabled={!dirty || saving}
            onClick={handleReset}
            sx={{ textTransform: "none" }}
          >
            Reset
          </Button>
          <Button
            variant="contained"
            disabled={!dirty || saving}
            onClick={handleSave}
            sx={{ textTransform: "none" }}
          >
            {saving
              ? "Saving…"
              : `Save Priorities${dirty ? ` (${changedOrgs.length})` : ""}`}
          </Button>
          {(saving || loading) && <CircularProgress size={20} />}
        </Box>
      </Box>

      {/* Top scale labels: Low / Neutral / High */}
      <Grid container alignItems="center" sx={{ mb: 1, px: 1 }}>
        <Grid item xs={3}>
          <ToggleButtonGroup
            size="small"
            exclusive
            value={sortMode}
            onChange={(_e, next) => {
              if (next) setSortMode(next);
            }}
            aria-label="Sort organizations"
          >
            <ToggleButton
              value="alpha"
              sx={{ textTransform: "none", py: 0.25, px: 1 }}
            >
              A–Z
            </ToggleButton>
            <ToggleButton
              value="value"
              sx={{ textTransform: "none", py: 0.25, px: 1 }}
            >
              Priority
            </ToggleButton>
          </ToggleButtonGroup>
        </Grid>
        <Grid item xs={8}>
          <Box sx={{ display: "flex", justifyContent: "space-between" }}>
            <Typography sx={scaleLabelSx}>Low</Typography>
            <Typography sx={scaleLabelSx}>Neutral</Typography>
            <Typography sx={scaleLabelSx}>High</Typography>
          </Box>
        </Grid>
        <Grid item xs={1} />
      </Grid>

      {/* Org rows */}
      {activeOrgs.length === 0 && !loading && (
        <Box sx={{ fontSize: 14, color: "#666", p: 2 }}>
          No active organizations found.
        </Box>
      )}

      {displayedOrgs.map((org) => {
        const value = priorities[org.id] ?? DEFAULT_PRIORITY;
        const isChanged = value !== getLoadedValue(org);
        return (
          <Grid
            container
            key={org.id}
            alignItems="center"
            sx={{ py: 1, px: 1, borderBottom: "1px solid #f0f0f0" }}
          >
            <Grid item xs={3}>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: isChanged ? 700 : 500,
                  color: isChanged ? "#1976d2" : "#020303",
                  pr: 2,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
                title={org.name}
              >
                {org.name || "(unnamed)"}
              </Typography>
            </Grid>
            <Grid item xs={8}>
              <Slider
                value={value}
                min={1}
                max={100}
                step={1}
                marks={false}
                valueLabelDisplay="auto"
                onChange={(_e, newValue) =>
                  handleSliderChange(org.id, newValue)
                }
                onChangeCommitted={handleSliderCommitted}
              />
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={endpointLabelSx}>0</Typography>
                <Typography sx={endpointLabelSx}>50</Typography>
                <Typography sx={endpointLabelSx}>100</Typography>
              </Box>
            </Grid>
            <Grid item xs={1}>
              <Typography
                sx={{
                  fontSize: 15,
                  fontWeight: 700,
                  textAlign: "right",
                  color: isChanged ? "#1976d2" : "#020303",
                }}
              >
                {value}
              </Typography>
            </Grid>
          </Grid>
        );
      })}
    </Box>
  );
};

export default OrgPrioritiesPanel;

const scaleLabelSx = {
  fontSize: 13,
  fontWeight: 700,
  color: "#666",
};

const endpointLabelSx = {
  fontSize: 12,
  color: "#999",
};
